import { m, useMotionValueEvent, useScroll } from "motion/react";
import * as React from "react";
import { cn } from "../../lib/utils";
import { SPRING_TRANSITION } from "../shared/constants";
import {
	type FloatingToolbarColors,
	getToolbarColors,
	type ToolbarVariant,
} from "./toolbar-colors";
import { ToolbarContext } from "./toolbar-context";

export interface BottomDockedToolbarProps {
	/** Color variant: standard, vibrant, surface-high, surface-highest, tertiary, or xr */
	variant?: ToolbarVariant;
	/** Custom colors override */
	colors?: FloatingToolbarColors;
	/** Whether to hide on scroll */
	hideOnScroll?: boolean;
	/** Scroll container ref for scroll behavior (if empty, uses window scroll) */
	scrollContainerRef?: React.RefObject<HTMLElement | null>;
	/** Start content (left-aligned) */
	startContent?: React.ReactNode;
	/** End content (right-aligned) */
	endContent?: React.ReactNode;
	/** Center content */
	children?: React.ReactNode;
	/**
	 * Horizontal padding (px) applied to the toolbar container.
	 * MD3 spec requires a minimum of 16dp on leading and trailing edges.
	 * @default 16
	 */
	paddingX?: number;
	/**
	 * Layout distribution of toolbar content.
	 * - `"between"` – leading/center/trailing spread across full width (default, compact screens).
	 * - `"center"` – all content centered; ideal for medium+ screen widths.
	 * - `"end-weighted"` – key action is centered while others are pushed to the edges.
	 * @default "between"
	 */
	justify?: "between" | "center" | "end-weighted";
	/**
	 * Container shape.
	 * - `"none"` – straight corners (MD3 default for mobile docked toolbars).
	 * - `"large"` – large rounded corners (recommended for web / large screens).
	 * - `"full"` – fully pill-shaped.
	 *
	 * MD3 guideline: *"On web and large screens, the docked toolbar can be rounded."*
	 * @default "none"
	 */
	shape?: "none" | "large" | "full";
	className?: string;
	"aria-label"?: string;
}

/**
 * A full-width docked toolbar typically fixed at the bottom of the screen.
 * Replaces the deprecated Bottom App Bar in MD3 Expressive.
 *
 * @example
 * ```tsx
 * <BottomDockedToolbar hideOnScroll startContent={<IconButton aria-label="Menu"><Icon name="menu" /></IconButton>}>
 *   <div className="flex gap-2">
 *     <IconButton aria-label="Search"><Icon name="search" /></IconButton>
 *     <IconButton aria-label="Edit"><Icon name="edit" /></IconButton>
 *   </div>
 * </BottomDockedToolbar>
 * ```
 */
export const BottomDockedToolbar = React.forwardRef<
	HTMLDivElement,
	BottomDockedToolbarProps
>(
	(
		{
			variant = "standard",
			colors: customColors,
			hideOnScroll = false,
			scrollContainerRef,
			startContent,
			endContent,
			children,
			paddingX = 16,
			justify = "between",
			shape = "none",
			className,
			"aria-label": ariaLabel,
			...props
		},
		ref,
	) => {
		const colors = getToolbarColors(variant, customColors);

		const { scrollY } = useScroll(
			scrollContainerRef ? { container: scrollContainerRef } : undefined,
		);

		const [isHidden, setIsHidden] = React.useState(false);
		const lastScrollY = React.useRef(0);

		useMotionValueEvent(scrollY, "change", (latest) => {
			if (!hideOnScroll) return;

			const diff = latest - lastScrollY.current;

			if (latest <= 0) {
				setIsHidden(false);
				lastScrollY.current = 0;
				return;
			}

			if (Math.abs(diff) > 10) {
				setIsHidden(diff > 0);
				lastScrollY.current = latest;
			}
		});

		const cssVars = {
			"--toolbar-bg": colors.toolbarContainerColor,
			"--toolbar-color": colors.toolbarContentColor,
		} as React.CSSProperties;

		const SHAPE_CLASSES = {
			none: "",
			large: "rounded-2xl",
			full: "rounded-full",
		};

		const justifyClass =
			justify === "center" ? "justify-center gap-4" : "justify-between";

		const isXr = variant === "xr";

		return (
			<ToolbarContext.Provider value={{ orientation: "horizontal" }}>
				<m.div
					ref={ref}
					role="toolbar"
					aria-label={ariaLabel || "Bottom Docked Toolbar"}
					initial={false}
					animate={{
						y: isHidden ? "100%" : "0%",
					}}
					transition={SPRING_TRANSITION}
					style={{
						...cssVars,
						backgroundColor: "var(--toolbar-bg)",
						color: "var(--toolbar-color)",
						// Apply safe area inset for mobile bottom indicators
						paddingBottom: "env(safe-area-inset-bottom, 0px)",
						paddingLeft: paddingX,
						paddingRight: paddingX,
					}}
					className={cn(
						"fixed bottom-0 left-0 right-0 w-full z-40 text-(--toolbar-color) [&_.text-m3-on-surface-variant]:text-current",
						"flex items-center h-16",
						justifyClass,
						SHAPE_CLASSES[shape],
						isXr
							? "backdrop-blur-md border-t border-white/10 shadow-lg"
							: "shadow-[0_-1px_3px_rgba(0,0,0,0.1)]",
						"pointer-events-auto",
						className,
					)}
					{...props}
				>
					<div className="flex items-center justify-start flex-1 shrink-0">
						{startContent}
					</div>

					<div
						className={cn(
							"flex items-center justify-center",
							justify === "center" ? "gap-1" : "flex-1 shrink-0",
						)}
					>
						{children}
					</div>

					<div className="flex items-center justify-end flex-1 shrink-0">
						{endContent}
					</div>
				</m.div>
			</ToolbarContext.Provider>
		);
	},
);
BottomDockedToolbar.displayName = "BottomDockedToolbar";
